/**
 * Drives filtering on a collection that sits past Shopify's 5.000-product limit, where
 * the platform serves no filters at all (SHO-9684). /collections/behang holds 15.919.
 *
 * Who owns what:
 *   - Liquid owns the rail markup. `blocks/filters.liquid` renders it from the collection
 *     metafield `collectie.facet_index`, through the theme's own `snippets/list-filter.liquid`,
 *     so there is no second copy of that markup to keep in step.
 *   - `assets/facets.js` owns the URL and the FilterUpdate event. It stands down from
 *     rendering when it finds a `[data-hds-facet-index]` ancestor — the same escape hatch
 *     the Algolia search results already use.
 *   - This file owns the numbers and the grid, and nothing else.
 *
 * Nothing here runs on an unfiltered page load: that is Shopify's own server-rendered
 * grid, and the index is only fetched once someone actually touches a filter.
 */

import { ThemeEvents } from '@theme/events';
import { restoreFacetsSheetMode } from '@theme/facets';
import {
  createFacetIndex,
  parseSelection,
  matchingProducts,
  facetCounts,
  relaxSuggestions,
  explainQuery,
  withoutQueryWord,
  PRICE_PARAM,
  QUERY_PARAM,
} from '@theme/facet-index-core';
import {
  applyChips,
  applyActiveCount,
  applySeeResultsCount,
  applyCounts,
  applyFacetLabels,
  applyOrder,
  applySelection,
  selectionForRail,
  NUMBER_IN_TEXT,
} from '@theme/facet-index-rail';
import {
  ensureSelectedRows,
  mountShowMore,
  mountValueSearch,
  RAIL_CHANGED,
} from '@theme/facet-index-values';

/**
 * Cards fetched at once. Each is one Section Rendering request against the CDN, and a page of
 * 24 is what stands between a filter click and a grid: at six the batch takes four rounds and
 * a good two seconds, at twelve half that, over one HTTP/2 connection either way.
 */
const FETCH_CONCURRENCY = 12;

/**
 * A pause after the last keystroke. Long enough that a word is typed out before anything
 * moves, short enough to feel like an answer rather than a submission.
 */
const QUERY_DEBOUNCE_MS = 250;

/**
 * Where a visitor has to touch before the index is worth fetching: the desktop rail, the
 * button that opens the mobile drawer, the drawer itself, or the free-text box.
 */
const RAIL_INTENT =
  'facets-form-component, [data-facets-drawer-root], .facets-toggle__button, [data-facets-sheet], [data-facet-index-query]';

/**
 * @param {string} name
 * @returns {boolean}
 */
function isFilterParam(name) {
  return name.startsWith('filter.');
}

/**
 * @returns {boolean} Whether the current URL asks for anything but the whole collection.
 */
function urlHasFilters() {
  const params = new URLSearchParams(window.location.search);
  for (const [key, value] of params.entries()) {
    if (isFilterParam(key) && value !== '') return true;
  }
  return (params.get(QUERY_PARAM) ?? '').trim() !== '';
}

/**
 * Pushes one event onto the GA4 dataLayer, if there is one.
 *
 * Filtering is where a collection is either useful or abandoned, and none of it is visible in
 * analytics today: every selection lives in a URL that never triggers a page view. These two
 * events are the minimum needed to see which filters are used and — the expensive one —
 * how often a visitor ends up staring at nothing.
 *
 * @param {string} event
 * @param {Record<string, unknown>} payload
 */
function track(event, payload) {
  const layer = /** @type {unknown[] | undefined} */ (window.dataLayer);
  if (!Array.isArray(layer)) return;
  layer.push({ event, ...payload });
}

/**
 * Resolves once the browser has had a frame to itself.
 *
 * `await` on an already-resolved promise is a microtask, which the browser runs before it
 * paints — so an `async` function that only awaits cached work never lets go of the frame at
 * all. This one does: the rAF callback runs just before the paint, and the timeout inside it
 * lands just after.
 *
 * @returns {Promise<void>}
 */
function afterPaint() {
  return new Promise((resolve) => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      resolve();
    };

    requestAnimationFrame(() => setTimeout(finish, 0));
    // A tab in the background never paints and never fires rAF; filtering still has to finish.
    setTimeout(finish, 50);
  });
}

/**
 * Runs `worker` over every item, at most `limit` in flight. Results keep input order so
 * the grid never reshuffles on a slow response.
 *
 * @template T, R
 * @param {T[]} items
 * @param {number} limit
 * @param {(item: T, index: number) => Promise<R>} worker
 * @returns {Promise<R[]>}
 */
async function mapWithLimit(items, limit, worker) {
  const results = new Array(items.length);
  let cursor = 0;

  const runners = Array.from({ length: Math.min(limit, items.length) }, async () => {
    while (cursor < items.length) {
      const index = cursor;
      cursor += 1;
      results[index] = await worker(items[index], index);
    }
  });

  await Promise.all(runners);
  return results;
}

class FacetIndexComponent extends HTMLElement {
  /** @type {import('./facet-index-core.js').FacetIndex | null} */
  #index = null;

  /** @type {Promise<import('./facet-index-core.js').FacetIndex> | null} */
  #loading = null;

  /** @type {string[]} */
  #handles = [];

  /** @type {number} */
  #rendered = 0;

  /**
   * Bumped on every apply, so a slow card fetch from a superseded selection is dropped
   * instead of landing in the grid.
   */
  #generation = 0;

  /** The card section's fully qualified id. See #resolveCardSection. */
  #cardSectionId = '';

  /** @type {Promise<string> | null} In flight, so two callers share one probe. */
  #cardSectionProbe = null;

  /** @type {{ handle: string, element: Element | null } | null} The card the probe brought back. */
  #probedCard = null;

  /** @type {Element | null} */
  #footerElement = null;

  /** @type {Record<string, Record<string, number>>} Last counts, for rows rendered after the fact. */
  #counts = {};

  /** @type {import('./facet-index-core.js').Selection | null} */
  #selection = null;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #queryTimer;

  /** @type {MutationObserver | null} */
  #drawerObserver = null;

  /** Whether the index has been fetched ahead of any filtering. See #warm. */
  #warming = false;

  connectedCallback() {
    const scope = this.closest('results-list');
    if (!scope) return;

    // FilterUpdateEvent bubbles up from facets-form-component, both the desktop rail and
    // the mobile drawer's copy of the form.
    scope.addEventListener(ThemeEvents.FilterUpdate, this.#onFilterUpdate);
    // Fired when searching within a filter swaps the rows out; the counts and ticks have to
    // go back onto the rows that replaced them.
    scope.addEventListener(RAIL_CHANGED, this.#onRailChanged);
    window.addEventListener('popstate', this.#onPopState);

    // Delegated: the mobile drawer is re-rendered by the theme, taking its copy of the search
    // box with it, and a listener bound to the element would go with it.
    document.addEventListener('input', this.#onQueryInput);
    document.addEventListener('keydown', this.#onQueryKeydown);

    this.#watchDrawer(scope);
    applyFacetLabels(scope);

    if (urlHasFilters()) {
      void this.#apply({ scroll: false });
      return;
    }

    // Nothing is filtered yet, so the grid stays Shopify's — but the rail is only half a rail
    // without the index: no search box inside a list of 2.170 dessins, and counts that ignore
    // every other choice.
    //
    // Waiting for the first click turned out to be too late. The index takes about six tenths
    // of a second to arrive and compile, so the rail a visitor first meets has no search boxes
    // and the collection's own counts, and the real rail appears underneath their hand — which
    // reads as filters that are there sometimes and not others. So: on the first move towards
    // the rail (a mouse has to cross it before it can click), and otherwise once the page has
    // gone idle. A visitor who never filters pays 307 KB after everything else is done.
    scope.addEventListener('pointerover', this.#onRailIntent);
    scope.addEventListener('pointerdown', this.#onRailIntent, { capture: true });
    scope.addEventListener('focusin', this.#onRailIntent);
    this.#warmWhenIdle();
  }

  /**
   * Fetches the index in the gap after the page has finished loading, so the rail is whole
   * before it is touched. Behind `load` as well as idle: `requestIdleCallback` can fire during
   * loading, and the index is not worth a byte of bandwidth the images still need.
   */
  #warmWhenIdle() {
    const schedule = () => {
      const idle = window.requestIdleCallback;
      if (typeof idle === 'function') idle(() => this.#warm(), { timeout: 3000 });
      else setTimeout(() => this.#warm(), 1200);
    };

    if (document.readyState === 'complete') schedule();
    else window.addEventListener('load', schedule, { once: true });
  }

  disconnectedCallback() {
    const scope = this.closest('results-list');
    scope?.removeEventListener(ThemeEvents.FilterUpdate, this.#onFilterUpdate);
    scope?.removeEventListener(RAIL_CHANGED, this.#onRailChanged);
    scope?.removeEventListener('pointerover', this.#onRailIntent);
    scope?.removeEventListener('pointerdown', this.#onRailIntent, { capture: true });
    scope?.removeEventListener('focusin', this.#onRailIntent);
    window.removeEventListener('popstate', this.#onPopState);
    document.removeEventListener('input', this.#onQueryInput);
    document.removeEventListener('keydown', this.#onQueryKeydown);
    this.#drawerObserver?.disconnect();
    clearTimeout(this.#queryTimer);
  }

  /**
   * The mobile drawer's rail is fetched the first time the drawer opens, and re-fetched on a
   * filter change — server-rendered from the metafield each time, which knows nothing of the
   * request. It therefore lands with empty boxes, unfiltered counts and no pills, whatever
   * this component wrote a moment earlier.
   *
   * Watched rather than hooked: `loadFacetsDrawerContent` only announces itself on the
   * Algolia search page, so there is no event to listen for here.
   *
   * @param {Element} scope
   */
  #watchDrawer(scope) {
    const root = scope.querySelector('[data-facets-drawer-root]');
    // Lab (lab/iphone-40-no-merge): the desktop rail arrives the same way, filled into this
    // placeholder by assets/hds-lazy-vertical-facets.js, and needs the same dressing.
    const vertical = scope.querySelector('[data-hds-vertical-filters]');
    if (!(root instanceof HTMLElement) && !(vertical instanceof HTMLElement)) return;

    this.#drawerObserver = new MutationObserver((records) => {
      for (const target of new Set(records.map((record) => record.target))) {
        if (target === root && !root.querySelector('facets-form-component')) continue;
        if (target instanceof Element) applyFacetLabels(target);
      }
      if (!this.#index) return;
      const selection = parseSelection(new URLSearchParams(window.location.search), this.#index);
      this.#selection = selection;
      this.#renderCounts(this.#index, selection);
    });
    if (root instanceof HTMLElement) this.#drawerObserver.observe(root, { childList: true });
    if (vertical instanceof HTMLElement) this.#drawerObserver.observe(vertical, { childList: true });
  }

  #onFilterUpdate = () => {
    void this.#apply({ scroll: true });
  };

  #onPopState = () => {
    void this.#apply({ scroll: false });
  };

  /**
   * @param {Event} event
   */
  #onRailIntent = (event) => {
    const target = event.target;
    if (!(target instanceof Element) || !target.closest(RAIL_INTENT)) return;
    this.#warm();
  };

  /**
   * Loads the index without touching the grid, and dresses the rail with it: real counts and
   * a search box in every list too long to read. Deliberately not busy — the grid on screen is
   * the server's own, complete and clickable, and has nothing to wait for.
   */
  #warm() {
    if (this.#warming) return;
    this.#warming = true;

    const generation = this.#generation;
    this.#loadIndex()
      .then((index) => {
        // A filter was clicked while this was in flight; that apply owns the rail now.
        if (generation !== this.#generation) return;

        const selection = parseSelection(new URLSearchParams(window.location.search), index);
        this.#selection = selection;
        this.#renderCounts(index, selection);
      })
      .catch((error) => {
        console.error('[facet-index] index laden mislukt', error);
      });
  }

  #onRailChanged = () => {
    const scope = this.closest('results-list');
    if (!scope || !this.#index) return;
    const selection = parseSelection(new URLSearchParams(window.location.search), this.#index);
    this.#selection = selection;
    applySelection(scope, this.#index, selection);
    applyCounts(scope, this.#index, this.#counts, document.documentElement.lang || 'nl');
    const drawer = scope.querySelector('#filters-drawer');
    if (drawer instanceof HTMLElement) restoreFacetsSheetMode(drawer);
  };

  /**
   * @param {Event} event
   */
  #onQueryInput = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.matches('[data-facet-index-query]')) return;

    const value = input.value;
    clearTimeout(this.#queryTimer);
    this.#queryTimer = setTimeout(() => this.#commitQuery(value), QUERY_DEBOUNCE_MS);
  };

  /**
   * @param {KeyboardEvent} event
   */
  #onQueryKeydown = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.matches('[data-facet-index-query]')) return;

    // The box sits inside the facets form, and this one does not submit anything: Enter only
    // means "now", so the debounce is cut short instead.
    if (event.key !== 'Enter') return;
    event.preventDefault();
    clearTimeout(this.#queryTimer);
    this.#commitQuery(input.value);
  };

  /**
   * @param {string} value
   */
  #commitQuery(value) {
    const url = new URL(window.location.href);
    const trimmed = value.trim();
    if ((url.searchParams.get(QUERY_PARAM) ?? '') === trimmed) return;

    if (trimmed === '') url.searchParams.delete(QUERY_PARAM);
    else url.searchParams.set(QUERY_PARAM, trimmed);

    // Replaced, not pushed: a word typed out letter by letter would otherwise leave eight
    // history entries between the visitor and the page they came from.
    history.replaceState(history.state, '', url.toString());
    void this.#apply({ scroll: false });
  }

  get #indexUrl() {
    return this.getAttribute('index-url') ?? '';
  }

  get #cardSection() {
    return this.getAttribute('card-section') ?? 'facet-index-card';
  }

  /**
   * The alternate product template the card section is configured in. A section rendered
   * outside a template has no block configuration, and `_product-card` draws its entire
   * contents from that configuration — without the view the card comes back an empty shell.
   */
  get #cardView() {
    return this.getAttribute('card-view') ?? 'facet-card';
  }

  get #pageSize() {
    return Number(this.getAttribute('page-size')) || 24;
  }

  get #grid() {
    return this.closest('results-list')?.querySelector('ul.product-grid') ?? null;
  }

  get #footer() {
    // Held rather than looked up: #takeOverGrid moves it out of this element, next to the grid.
    if (!this.#footerElement) this.#footerElement = this.querySelector('[data-facet-index-footer]');
    return this.#footerElement;
  }

  get #status() {
    return this.querySelector('[data-facet-index-status]');
  }

  /**
   * Fetched once per page, and only when someone reaches for a filter — the unfiltered page
   * is the one crawlers and most visitors see, and it must not pay for this.
   *
   * The busy flag is the caller's: filtering waits on this and must not accept clicks
   * meanwhile, while a warm-up sits behind a grid that is complete and clickable already.
   *
   * @returns {Promise<import('./facet-index-core.js').FacetIndex>}
   */
  #loadIndex() {
    if (this.#index) return Promise.resolve(this.#index);
    if (this.#loading) return this.#loading;

    this.#loading = fetch(this.#indexUrl, { headers: { Accept: 'application/json' } })
      .then((response) => {
        if (!response.ok) throw new Error(`Facet-index ${response.status} op ${this.#indexUrl}`);
        return response.json();
      })
      .then((raw) => {
        this.#index = createFacetIndex(raw);
        return this.#index;
      })
      .catch((error) => {
        this.#loading = null;
        throw error;
      });

    return this.#loading;
  }

  /**
   * @param {{ scroll: boolean }} options
   */
  async #apply({ scroll }) {
    const generation = (this.#generation += 1);

    this.#dropEmptyQuery();

    // No filters left: hand the page back to Shopify rather than rebuilding 15.919 cards.
    if (!urlHasFilters()) {
      if (this.#hasTakenOver()) window.location.assign(window.location.href);
      return;
    }

    // Alongside the index rather than after it: the probe is a page render, and which product
    // it runs on does not matter, so the grid Shopify already served is a fine source for one.
    const probeHandle = this.#anyRenderedHandle();
    if (probeHandle) void this.#resolveCardSection(probeHandle).catch(() => {});

    this.#setBusy(true);

    // Hand the frame back before doing any of the work.
    //
    // A click on a checkbox ticks it, and the browser paints that tick at the next frame — but
    // only once the main thread is free. Everything below runs in the same task the click
    // started (a cached index resolves as a microtask, which is not a yield), so the tick, the
    // busy state and the skeletons all waited for the counting and the rail to finish. What
    // the visitor saw was a click that did nothing, then everything at once. One yield puts
    // the tick on screen first, and costs a single frame.
    await afterPaint();

    let index;
    try {
      index = await this.#loadIndex();
    } catch (error) {
      console.error('[facet-index] index laden mislukt', error);
      this.#setBusy(false);
      return;
    }

    if (generation !== this.#generation) return;

    // Read the URL at write-time, not from a snapshot taken before `afterPaint`: a later
    // click already pushed a newer search, and ticking from the old one puts B and C back.
    const params = new URLSearchParams(window.location.search);
    const selection = parseSelection(params, index);
    this.#selection = selection;

    if (generation !== this.#generation) return;

    // Sorting was the one control on this page that did nothing: the theme renders the select,
    // `facets.js` puts `sort_by` in the URL, and the server would have applied it — except the
    // server no longer renders this grid. The order comes out of the index instead.
    const sorted = matchingProducts(index, selection, params.get('sort_by') ?? '');
    this.#handles = sorted.map((productIndex) => index.handles[productIndex]);
    this.#rendered = 0;

    if (generation !== this.#generation) return;

    this.#renderCounts(index, selection);
    this.#renderTotal(this.#handles.length);
    this.#track(params, this.#handles.length);
    await this.#renderMore({ replace: true, generation });

    if (scroll) this.#scrollToGrid();
  }

  /**
   * Takes an empty `zoek=` back out of the URL.
   *
   * The search box lives in the facets form so its term survives the next checkbox click,
   * and `facets.js` builds the URL out of that form — which means an untouched box still
   * contributes `zoek=` to every filter URL, and to everything the visitor copies or shares.
   */
  #dropEmptyQuery() {
    const url = new URL(window.location.href);
    if (url.searchParams.get(QUERY_PARAM) !== '') return;

    url.searchParams.delete(QUERY_PARAM);
    history.replaceState(history.state, '', url.toString());
  }

  /**
   * @param {URLSearchParams} params
   * @param {number} total
   */
  #track(params, total) {
    /** @type {Record<string, string[]>} */
    const filters = {};
    for (const [key, value] of params.entries()) {
      if (!isFilterParam(key) || value === '') continue;
      (filters[key] ??= []).push(value);
    }

    const query = (params.get(QUERY_PARAM) ?? '').trim();
    const detail = {
      collection: this.closest('[data-hds-facet-index]')?.getAttribute('data-hds-facet-index') ?? '',
      filters,
      filter_count: Object.values(filters).reduce((sum, values) => sum + values.length, 0),
      search_term: query,
      sort_by: params.get('sort_by') ?? '',
      results: total,
    };

    track('collection_filter', detail);
    // Its own event, because this is the one worth alerting on: a combination nobody can
    // satisfy is either a gap in the range or a gap in the data.
    if (total === 0) track('collection_filter_no_results', detail);
  }

  /**
   * True once this component has replaced the server-rendered grid, which is what makes
   * a full reload the honest way back to the unfiltered page.
   */
  #hasTakenOver() {
    return this.#grid?.hasAttribute('data-facet-index-owned') ?? false;
  }

  /**
   * Rewrites every count in the rail and greys out what would lead nowhere. The engine
   * counts disjunctively, so picking one brand leaves the other brands clickable.
   *
   * @param {import('./facet-index-core.js').FacetIndex} index
   * @param {import('./facet-index-core.js').Selection} selection
   */
  #renderCounts(index, selection) {
    const scope = this.closest('results-list');
    if (!scope) return;

    const locale = document.documentElement.lang || 'nl';
    const live = selectionForRail(index, selection);
    this.#selection = live;

    // Both rails carry the same input names, so the drawer's copy is updated too.
    const counts = facetCounts(index, live);
    this.#counts = counts;

    // Before the ticks go on: a ticked value the rail did not ship has no row to tick.
    ensureSelectedRows(scope, index, live);

    applySelection(scope, index, live);
    this.#syncQueryInputs(scope);
    this.#renderReading(scope, index, live);
    applyCounts(scope, index, counts, locale);
    applyOrder(scope, index, counts);

    applyChips(scope, index, live, {
      template: this.querySelector('template[data-facet-index-chip]'),
      url: new URL(window.location.href),
      clearAllText: this.getAttribute('clear-all-text') ?? '',
    });

    let active = 0;
    for (const chosen of live.fields.values()) active += chosen.size;
    if (live.priceMin != null || live.priceMax != null) active += 1;
    if (live.query !== '') active += 1;
    applyActiveCount(scope, active, this.#resultTotal(index, live), locale);

    mountValueSearch(scope, index, {
      label: this.getAttribute('value-search-label') ?? 'Zoek',
      placeholder: this.getAttribute('value-search-placeholder') ?? 'Zoek in %s',
      empty: this.getAttribute('value-search-empty') ?? 'Niets gevonden',
    });

    mountShowMore(scope, index);

    const drawer = scope.querySelector('#filters-drawer');
    if (drawer instanceof HTMLElement) restoreFacetsSheetMode(drawer);
  }

  /**
   * Puts the term from the URL in every copy of the search box — the one that was typed in
   * excepted, which is already ahead of it.
   *
   * @param {ParentNode} scope
   */
  /**
   * Says out loud how the sentence was read: which word became which filter, and which one
   * matched nothing at all.
   *
   * A search that quietly turns "groen" into a colour filter is a search nobody can correct.
   * One product out of 15.759 is a confusing answer until you can see that four words each
   * narrowed it — and a word marked as matching nothing is usually a typo, spotted in the
   * one place the visitor is already looking.
   *
   * @param {Element} scope
   * @param {import('./facet-index-core.js').FacetIndex} index
   * @param {import('./facet-index-core.js').Selection} selection
   */
  #renderReading(scope, index, selection) {
    const nodes = scope.querySelectorAll('[data-facet-index-reading]');
    if (nodes.length === 0) return;

    const readings = selection.query === '' ? [] : explainQuery(index, selection.query);
    const inTitle = this.getAttribute('reading-title') ?? 'titel';
    const none = this.getAttribute('reading-none') ?? 'geen treffer';
    const prefix = this.getAttribute('reading-prefix') ?? '';

    // Nothing to explain about a single word that landed somewhere: the count says it all.
    // Words the engine read as alternatives always are worth it, even when they are the whole
    // query — "arte eijffinger" was silently turned into "arte OF eijffinger".
    const worth =
      readings.length > 1 || readings.some((reading) => reading.count === 0 || reading.terms.length > 1);
    const text = readings
      .map((reading) => `${reading.term} → ${reading.field ?? (reading.inTitle ? inTitle : none)}`)
      .join(' · ');

    for (const node of nodes) {
      if (!(node instanceof HTMLElement)) continue;
      node.hidden = !worth;
      node.textContent = worth ? `${prefix}${prefix ? ' ' : ''}${text}` : '';
    }
  }

  #syncQueryInputs(scope) {
    const raw = new URLSearchParams(window.location.search).get(QUERY_PARAM) ?? '';
    for (const input of scope.querySelectorAll('[data-facet-index-query]')) {
      if (!(input instanceof HTMLInputElement) || input === document.activeElement) continue;
      if (input.value !== raw) input.value = raw;
    }
  }

  /**
   * Matching product count for the current selection. `#handles` is only filled in `#apply`;
   * the drawer observer and `#warm` still need a real total for the see-results CTA.
   *
   * @param {import('./facet-index-core.js').FacetIndex} index
   * @param {import('./facet-index-core.js').Selection} selection
   * @returns {number}
   */
  #resultTotal(index, selection) {
    if (this.#handles.length > 0) return this.#handles.length;

    const params = new URLSearchParams(window.location.search);
    return matchingProducts(index, selection, params.get('sort_by') ?? '').length;
  }

  /**
   * @param {number} total
   */
  #renderTotal(total) {
    const grid = this.#grid;
    if (grid) grid.setAttribute('data-total-items', String(total));

    const locale = document.documentElement.lang || 'nl';
    applySeeResultsCount(total, locale);

    const template = total === 1 ? this.getAttribute('count-one') : this.getAttribute('count-other');
    if (!template) return;

    const formatted = new Intl.NumberFormat(locale).format(total);
    const text = template.replace(NUMBER_IN_TEXT, formatted);

    for (const node of document.querySelectorAll('[data-testid="products-count"] span')) {
      node.textContent = text;
    }

    // What a screen reader needs after a filter changes is the new count, not word that
    // something is loading — so the live region carries that instead.
    const status = this.#status;
    if (status) status.textContent = text;
  }

  /**
   * Renders the next page of cards. Each card is its own Section Rendering request, so
   * they come back as the theme's real product card and stay cacheable per product.
   *
   * @param {{ replace: boolean, generation: number }} options
   */
  async #renderMore({ replace, generation }) {
    const grid = this.#grid;
    if (!grid) return;

    const remaining = () => this.#handles.length - this.#rendered;

    // A selection that matches nothing still has to clear the server-rendered grid.
    // Everything else waits for the card probe: emptying first left a blank page when
    // the template was missing.
    if (remaining() === 0) {
      if (replace) this.#takeOverGrid(grid);
      this.#setBusy(false);
      this.#renderFooter();
      return;
    }

    this.#setBusy(true);

    try {
      await this.#resolveCardSection(this.#handles[this.#rendered]);
    } catch (error) {
      console.error('[facet-index] kaartsectie niet gevonden', error);
      this.#setBusy(false);
      const status = this.#status;
      if (status) status.textContent = 'Gefilterde resultaten konden niet geladen worden.';
      return;
    }

    if (generation !== this.#generation) return;

    if (replace) this.#takeOverGrid(grid);
    const positionBase = grid.querySelectorAll(':scope > li.product-grid__item').length;
    this.#renderSkeletons(grid, Math.min(this.#pageSize, remaining()));

    const fragment = document.createDocumentFragment();
    let appended = 0;
    let position = positionBase;

    while (appended < this.#pageSize && this.#rendered < this.#handles.length) {
      if (generation !== this.#generation) return;

      const slice = this.#handles.slice(
        this.#rendered,
        this.#rendered + (this.#pageSize - appended)
      );
      const markup = await mapWithLimit(slice, FETCH_CONCURRENCY, (handle) => this.#fetchCard(handle));
      if (generation !== this.#generation) return;

      for (const card of markup) {
        if (!card) continue;
        // The card section cannot know where in a filtered result set its product landed; this
        // is the only place that does, and `sections/main-collection.liquid` puts it on every
        // card it renders.
        card.classList.add(`product-grid__item--${position}`);
        card.setAttribute('data-product-card-index', String(position));
        position += 1;
        fragment.append(card);
        appended += 1;
      }

      // Advance past the whole slice, including handles that came back empty: retrying a
      // deleted product on every "Toon meer" would stall the cursor on a hole.
      this.#rendered += slice.length;
    }

    this.#clearSkeletons(grid);
    grid.append(fragment);
    this.#setBusy(false);
    this.#renderFooter();
  }

  /**
   * Fills the emptied grid with plain grey stand-ins while the cards are fetched.
   *
   * The grid used to keep the previous selection's cards at 60% opacity, which reads as "these
   * are your results, dimmed" — the one thing they are not. A skeleton of the right shape says
   * the same thing about waiting without showing a single wrong product, and holds the page's
   * height so nothing below it jumps when the cards land.
   *
   * @param {Element} grid
   * @param {number} count
   */
  #renderSkeletons(grid, count) {
    const template = this.querySelector('template[data-facet-index-skeleton]');
    if (!(template instanceof HTMLTemplateElement) || count <= 0) return;

    const fragment = document.createDocumentFragment();
    for (let position = 0; position < count; position += 1) {
      fragment.append(template.content.cloneNode(true));
    }
    grid.append(fragment);
  }

  /**
   * @param {Element} grid
   */
  #clearSkeletons(grid) {
    for (const skeleton of grid.querySelectorAll('[data-facet-index-skeleton-item]')) skeleton.remove();
  }

  /**
   * Any product handle out of the grid as it stands. Null only on an empty collection, where
   * there is nothing to filter either and the probe waits for the index to name a product.
   *
   * @returns {string | null}
   */
  #anyRenderedHandle() {
    const href = this.#grid?.querySelector('a[href*="/products/"]')?.getAttribute('href');
    return href?.match(/\/products\/([^/?#]+)/)?.[1] ?? null;
  }

  /**
   * Empties the server-rendered grid and marks it as ours.
   *
   * @param {Element} grid
   */
  #takeOverGrid(grid) {
    grid.replaceChildren();
    grid.setAttribute('data-facet-index-owned', '');
    // Marks the whole results-list, which is what the stylesheet in
    // `snippets/facet-index-mount.liquid` hangs the theme's own paging off.
    const list = this.closest('results-list');
    list?.setAttribute('data-facet-index-owned', '');
    // CSS hides the pager, but an in-flight infinite-scroll fetch would still append
    // Shopify's page 2 into this grid. The list stands that observer down itself.
    list?.standDownForFacetIndex?.();

    // The mount sits in the section's own grid, where a footer lands below the filter rail —
    // 4.000 pixels under the last card. Right after the product grid is where the theme puts
    // its own paging and its "geen producten gevonden", and where these belong too.
    const footer = this.#footer;
    if (footer && footer.previousElementSibling !== grid) grid.after(footer);
  }

  /**
   * Section Rendering hands back a section stripped of its block configuration when it is
   * asked for by file name, and `_product-card` is nothing but its block configuration — so
   * the section has to be asked for by the id its template minted for it,
   * `template--<n>__facet-index-card`. That number belongs to the theme, and a duplicated
   * theme gets a new one, so it is read from a real render of the card template instead of
   * being baked in here where a duplicate would silently empty every card.
   *
   * That render costs a page — a second, most of it the theme's own header — so it happens
   * once: the id is kept in sessionStorage for the rest of the visit, and the card the probe
   * brought back is kept for `#fetchCard` rather than fetched again.
   *
   * @param {string} handle A product to render the card template for.
   * @returns {Promise<string>}
   */
  #resolveCardSection(handle) {
    if (this.#cardSectionId) return Promise.resolve(this.#cardSectionId);

    if (!this.#cardSectionProbe) {
      this.#cardSectionProbe = this.#probeCardSection(handle).catch((error) => {
        this.#cardSectionProbe = null;
        throw error;
      });
    }
    return this.#cardSectionProbe;
  }

  /**
   * @param {string} handle
   * @returns {Promise<string>}
   */
  async #probeCardSection(handle) {
    // Keyed on the theme: duplicate a theme and every template in it is minted a new id.
    const cacheKey = `facet-index:card-section:${window.Shopify?.theme?.id ?? ''}:${this.#cardView}`;
    const cached = sessionStorage.getItem(cacheKey);
    if (cached) {
      this.#cardSectionId = cached;
      return cached;
    }

    const root = window.Shopify?.routes?.root ?? '/';
    const url = `${root}products/${encodeURIComponent(handle)}?view=${encodeURIComponent(this.#cardView)}`;

    const response = await fetch(url);
    if (!response.ok) throw new Error(`Kaartsjabloon ${response.status} op ${url}`);

    const html = await response.text();
    const id = new RegExp(`shopify-section-(template--\\d+__${this.#cardSection})`).exec(html)?.[1];
    if (!id) throw new Error(`Geen sectie-id voor ${this.#cardSection} in ${url}`);

    this.#cardSectionId = id;
    sessionStorage.setItem(cacheKey, id);
    this.#probedCard = {
      handle,
      element: new DOMParser().parseFromString(html, 'text/html').querySelector('li.product-grid__item'),
    };
    return id;
  }

  /**
   * @param {string} handle
   * @returns {Promise<Element | null>}
   */
  async #fetchCard(handle) {
    if (this.#probedCard?.handle === handle) {
      const { element } = this.#probedCard;
      this.#probedCard = null;
      if (element) return element;
    }

    const root = window.Shopify?.routes?.root ?? '/';
    const url =
      `${root}products/${encodeURIComponent(handle)}` +
      `?section_id=${encodeURIComponent(this.#cardSectionId)}&view=${encodeURIComponent(this.#cardView)}`;

    try {
      const response = await fetch(url);
      if (!response.ok) return null;

      const html = await response.text();
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      return parsed.querySelector('li.product-grid__item');
    } catch (error) {
      console.error(`[facet-index] kaart ${handle} laden mislukt`, error);
      return null;
    }
  }

  #renderFooter() {
    const footer = this.#footer;
    if (!footer) return;

    // Emptying the grid takes the theme's own "geen producten gevonden" with it, so it is
    // put back here — the same block, out of the template in `facet-index-mount.liquid`.
    if (this.#handles.length === 0) {
      const empty = this.querySelector('template[data-facet-index-empty]');
      footer.replaceChildren(empty instanceof HTMLTemplateElement ? empty.content.cloneNode(true) : '');
      this.#renderRelax(footer.querySelector('[data-facet-index-relax]'));
      return;
    }

    const remaining = this.#handles.length - this.#rendered;
    if (remaining <= 0) {
      footer.replaceChildren();
      return;
    }

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'button button-secondary';
    button.textContent = this.getAttribute('load-more-text') ?? 'Toon meer';
    button.addEventListener('click', () => {
      void this.#renderMore({ replace: false, generation: this.#generation });
    });

    footer.replaceChildren(button);
  }

  /**
   * Turns a dead end into a way out: one button per active filter, saying what dropping it
   * would give back.
   *
   * "Gebruik minder filters" is the theme's answer, and with 22 groups it is no answer at all —
   * the visitor has no way of knowing which of their choices is the one that empties the set.
   * The engine does know, because it counts every combination anyway.
   *
   * @param {Element | null} container
   */
  /**
   * Goes to a filter URL the way the rail does: through the facets form, so the history entry
   * and the FilterUpdate event are the ones every other control produces. Falls back to a plain
   * navigation if the form is not there — better a reload than a dead button.
   *
   * @param {string} url
   */
  #applyUrl(url) {
    const form = this.closest('results-list')?.querySelector('facets-form-component');
    const update = /** @type {{ updateFiltersByURL?: (url: string) => void }} */ (form)?.updateFiltersByURL;

    if (typeof update === 'function') update.call(form, url);
    else window.location.assign(url);
  }

  #renderRelax(container) {
    if (!container || !(container instanceof HTMLElement) || !this.#index || !this.#selection) return;

    const suggestions = relaxSuggestions(this.#index, this.#selection);
    container.replaceChildren();
    container.hidden = suggestions.length === 0;
    if (suggestions.length === 0) return;

    const title = this.getAttribute('relax-title');
    if (title) {
      const heading = document.createElement('p');
      heading.className = 'facet-index__relax-title';
      heading.textContent = title;
      container.append(heading);
    }

    const paramById = new Map(this.#index.fields.map((field) => [field.id, field.param]));
    const formatter = new Intl.NumberFormat(document.documentElement.lang || 'nl');

    // Four is enough to find the culprit; a full list of every active filter is the wall the
    // visitor just walked into, restated.
    for (const suggestion of suggestions.slice(0, 4)) {
      const url = new URL(window.location.href);

      if (suggestion.id === PRICE_PARAM) {
        url.searchParams.delete(`${PRICE_PARAM}.gte`);
        url.searchParams.delete(`${PRICE_PARAM}.lte`);
      } else if (suggestion.id === QUERY_PARAM) {
        url.searchParams.delete(QUERY_PARAM);
      } else if (suggestion.id.startsWith(`${QUERY_PARAM}:`)) {
        // One word out of the sentence, rewritten by the engine — it owns which words are
        // joiners, and a joiner without its other half has to go with it.
        const dropped = suggestion.id.slice(QUERY_PARAM.length + 1);
        const kept = withoutQueryWord(url.searchParams.get(QUERY_PARAM) ?? '', dropped);

        if (kept === '') url.searchParams.delete(QUERY_PARAM);
        else url.searchParams.set(QUERY_PARAM, kept);
      } else {
        const param = paramById.get(suggestion.id);
        if (!param) continue;
        url.searchParams.delete(param);
      }

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'button button-secondary';
      button.textContent = `${suggestion.label} loslaten (${formatter.format(suggestion.count)})`;
      // A pill gets away with the theme's `on:click="/removeFilter"`; this cannot. That handler
      // resolves the form with `this.closest('facets-form-component')`, and these buttons stand
      // where the grid was — outside the form, where the search came up empty. So the same last
      // step is taken directly: one history entry and one FilterUpdate, no reload.
      button.addEventListener('click', () => this.#applyUrl(url.toString()));

      container.append(button);
    }
  }

  /**
   * The live region is left to `#renderTotal`, which already holds the new count by the
   * time cards start arriving. Announcing "loading" in between would only displace it.
   *
   * @param {boolean} busy
   */
  #setBusy(busy) {
    this.toggleAttribute('data-busy', busy);
  }

  /**
   * Brings the results back on screen after a filter — but only when they left it.
   *
   * On desktop the grid stands beside the rail and is in view the whole time, so scrolling
   * "to" it moved the page a little on every single tick and opened a band of white above the
   * cards. The jump is worth it only in the case it was written for: filtering from far down
   * the list, where the new results would otherwise be somewhere off screen.
   */
  #scrollToGrid() {
    const target = this.closest('results-list');
    if (!(target instanceof HTMLElement)) return;

    requestAnimationFrame(() => {
      const rect = target.getBoundingClientRect();
      // Measured after the grid was replaced, so a collection that shrank to three cards is
      // already three cards tall here.
      if (rect.top < window.innerHeight && rect.bottom > 0) return;

      const top = Math.max(0, rect.top + window.scrollY - 120);
      window.scrollTo({ top, left: 0, behavior: 'instant' });
    });
  }
}

if (!customElements.get('facet-index')) customElements.define('facet-index', FacetIndexComponent);
